// Promo attention-grabber. Two states, flipped by the cart:
//   • "available" — a promo is active but the cart doesn't qualify yet → a nudge
//     with a live countdown to the promo's end ("add a machine to see it").
//   • "applied"   — the cart qualifies → the celebratory message with the
//     estimated savings.
// Both customer and employee mode. Driven by store.promoBanner (which honors the
// hidden ?promo_preview test trigger).

import { useEffect, useState } from "react";
import { motion } from "framer-motion";

import { useConfigurator } from "../store";

function formatRemaining(ms: number): string {
  if (ms <= 0) return "0s";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
  return `${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
}

export function PromoBanner() {
  const { promoBanner } = useConfigurator();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const endsAt = promoBanner.promo?.ends_at ?? null;

  // Tick once a second while a countdown is on screen.
  useEffect(() => {
    if (!endsAt || promoBanner.status !== "available") return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt, promoBanner.status]);

  const promo = promoBanner.promo;
  if (!promo || promoBanner.status === null) return null;

  const applied = promoBanner.status === "applied";

  let message: string;
  if (applied) {
    const savings = `$${Math.round(
      promoBanner.savingsCents / 100,
    ).toLocaleString("en-US")}`;
    message = (promo.applied_message || "Promotion applied — you save {savings}!")
      .replace("{savings}", savings);
  } else {
    const countdown = endsAt
      ? formatRemaining(new Date(endsAt).getTime() - nowMs)
      : "";
    message = (
      promo.nudge_message || "Active promo — add a machine to see the discount."
    ).replace("{countdown}", countdown);
  }

  return (
    <motion.div
      key={applied ? "applied" : "available"}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={
        "mb-5 flex items-center gap-3 rounded-lg px-4 py-3 font-sans text-[15px] font-semibold " +
        (applied
          ? "bg-[#FAAD3D] text-ink shadow-[0_2px_12px_rgba(250,173,61,0.45)]"
          : "border border-[#FAAD3D] bg-[#FFF7E8] text-ink")
      }
      role="status"
      aria-live="polite"
    >
      <span aria-hidden className="text-[18px] leading-none">
        {applied ? "🎉" : "⏳"}
      </span>
      <span>{message}</span>
    </motion.div>
  );
}
